import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiLoader } from "react-icons/fi";
import { Modal } from "../../Components/Modal";
import { Button } from "../../Components/Button";
import { useApi } from "../../Common/Hooks/useApi";
import { DueDiligencePlanChangePreview } from "../../Common/Hooks/useApi/Schemas";
import { isErr } from "../../Common/Utils/Result";
import { formatMoney, planNameKey } from "../../Common/Utils/dueDiligencePlans";
import { eventBus } from "../../Common/EventBus";
import { Notification } from "../../Components/Notifications";

interface ChangePlanConfirmModalProps {
  /** The plan to move up to, by Stripe lookup key. */
  lookupKey: string;
  onClose: () => void;
  /** Stripe charged it: the plan and the credits arrive with the webhook. */
  onChanged: (lookupKey: string) => void;
}

/**
 * What moving up to a bigger plan charges today and the credits it brings,
 * asked to Stripe before confirming; confirming charges the card on file.
 */
export const ChangePlanConfirmModal = ({ lookupKey, onClose, onChanged }: ChangePlanConfirmModalProps) => {
  const { t, i18n } = useTranslation();
  const { getDueDiligencePlanChangePreviewEndpoint, changeDueDiligencePlanEndpoint } = useApi();
  const [preview, setPreview] = useState<DueDiligencePlanChangePreview | null>(null);
  const [hasFailedPreview, setHasFailedPreview] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const plan = t(planNameKey(lookupKey));

  useEffect(() => {
    const loadPreview = async () => {
      const result = await getDueDiligencePlanChangePreviewEndpoint(lookupKey);
      if (isErr(result)) {
        setHasFailedPreview(true);
        return;
      }
      setPreview(result.value.preview);
    };
    loadPreview();
  }, [getDueDiligencePlanChangePreviewEndpoint, lookupKey]);

  const handleConfirm = async () => {
    if (isChanging) {
      return;
    }
    setIsChanging(true);
    const result = await changeDueDiligencePlanEndpoint(lookupKey);
    if (isErr(result)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "error",
          result.error.errorCode === "DUE_DILIGENCE_PLAN_PAYMENT_FAILED"
            ? t("dueDiligencePlans.changePlan.notifications.paymentFailed")
            : t("dueDiligencePlans.changePlan.notifications.changeFailed")
        )
      );
      setIsChanging(false);
      return;
    }
    eventBus.emit(
      "NOTIFICATION",
      Notification.buildNotification("success", t("dueDiligencePlans.changePlan.notifications.changed", { plan }))
    );
    onChanged(lookupKey);
  };

  return (
    <Modal isOpen onClose={isChanging ? undefined : onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem", width: "100%", maxWidth: "480px" }}>
        <h2 style={{ fontSize: "1.5rem", fontWeight: 600, color: "#111827", margin: 0 }}>
          {t("dueDiligencePlans.changePlan.title", { plan })}
        </h2>

        {!preview && !hasFailedPreview && (
          <p style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#6B7280", margin: 0 }}>
            <FiLoader size={16} />
            {t("dueDiligencePlans.changePlan.loading")}
          </p>
        )}
        {hasFailedPreview && (
          <p style={{ color: "#B91C1C", margin: 0 }}>{t("dueDiligencePlans.changePlan.previewFailed")}</p>
        )}
        {preview && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", color: "#374151", lineHeight: 1.6 }}>
            <p style={{ margin: 0 }}>
              {t("dueDiligencePlans.changePlan.charge", {
                amount: formatMoney(preview.amountDue / 100, preview.currency, i18n.language),
              })}
            </p>
            <p style={{ margin: 0 }}>
              {t("dueDiligencePlans.changePlan.credits", { count: preview.extraMonthlyCredits })}
            </p>
            <p style={{ margin: 0, color: "#6B7280" }}>{t("dueDiligencePlans.changePlan.cycle")}</p>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <Button
            onClick={handleConfirm}
            disabled={!preview}
            isLoading={isChanging}
            style={{ backgroundColor: "#3B82F6", color: "#ffffff", width: "100%" }}
          >
            {t("dueDiligencePlans.changePlan.confirmButton")}
          </Button>
          <Button
            onClick={onClose}
            disabled={isChanging}
            style={{ backgroundColor: "transparent", color: "#6B7280", width: "100%" }}
          >
            {t("dueDiligencePlans.changePlan.cancelButton")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
